import { useEffect, useState, useContext } from 'react';
import { TemplateType } from './TemplateType';
import { useLocation } from 'react-router-dom';
import { PdfConfigContext } from 'common/context';
import { APP_CONFIG } from 'appConfig';
import { ApiResponse } from 'stores/apiStore';
import { logError } from '../helpers/helpers';
import { snakeCaseToUpperCase } from 'helpers/helpers';
import { API_CLIENT_SIGN_PDF_DATA, API_CLIENT_REQUEST_PDF_DATA, API_STATEMENT_PDF_DATA } from 'api/endpoints';
import { RootStore } from 'stores/rootStore';
import { PdfCategory } from './Interfaces';

// TODO: remove mock option?
const mockPdfContent = {
  styleguide: null,
  mainContract: null,
  priceList: null,
  newPriceList: null,
  investmentQuestionnaire: null,
  newInvestmentQuestionnaire: null,
  financialQuestionnaire: null,
  newFinancialQuestionnaire: null,
  meetingRecord: null,
  investmentPlan: null,
  newInvestmentPlan: null,
  detailInvestmentPlan: null,
  amlQuestionnaire: null,
  moneyWithdrawal: null,
  monthlyStatement: null,
  yearlyStatement: null,
  incomeOwnership: null,
  auditPage: null,
  personalData: null,
  newPersonalData: null,
  request: null,
  contractTermination: null,
  ltipAgreement: null,
  employerPayment: null,
  draftOverview: null,
  ltipTermination: null,
  contractAmendment: null,
  updateSettings: null,
  informationSheet: null,
  moneyTransfer: null,
};

function useEncodedId() {
  const id = new URLSearchParams(useLocation().search).get('id') as string;
  return encodeURIComponent(id);
}

function logFetchEventTime(event: string, url: string) {
  if (APP_CONFIG.debugPdfApiCalls) {
    console.log(`${event} | ${new Date().getTime()} | ${url}`); // eslint-disable-line no-console
  }
}

const useMockData = APP_CONFIG.pdfUseMockData;

export interface PdfContentParams {
  category?: PdfCategory;
  afterLoad?: () => void;
  accountId?: string;
  //requestId?: string;
  docId?: string; // request id or document id
  change?: boolean;
  rootStore?: RootStore;
  onError?: () => void;
  type?: string;
  //date?: string;
  preview?: boolean;
}

function usePdfContent<T>(params: PdfContentParams & { templateType: TemplateType }) {

  const { category, templateType, accountId, docId, change, rootStore, onError, preview } = params;
  const [pdfContent, setPdfContent] = useState<ApiResponse<T>>();

  const id = useEncodedId();
  const dataPart = '/data';
  const url = `${APP_CONFIG.apiUrl}/${APP_CONFIG.apiVersion}/basic/pdf/${templateType}${dataPart}?id=${id}`;
  const pdfConfig = useContext(PdfConfigContext);
  const encryptedCredentials = btoa(`${pdfConfig.basicAuthUsername}:${pdfConfig.basicAuthPassword}`);

  useEffect(() => {

    async function getPdfContent() {
      logFetchEventTime('FETCH_STARTED', url);

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Basic ${encryptedCredentials}`,
        },
      });

      logFetchEventTime('FETCH_ENDED', url);

      try {
        const body: ApiResponse<T> = await response.json();

        if (response.status >= 400) {
          let err = `Url: ${url}\nStatus: ${response.status}`;
          if (response.status < 500) {
            err += `\n\n${JSON.stringify(body, null, 2)}`;
          }
          logError('api.error', err);

          return;
        }

        logFetchEventTime('JSON_PARSED', url);
        setPdfContent(body);
      } catch (err) {
        logError('parse.error', `Url: ${url}\nStatus: ${response.status}\n\n${err}`);
      }
    }

    async function getViewPdfContent() {
      if (!rootStore) return;

      let endpoint = '';
      if (category === PdfCategory.REQUEST && docId) {
        endpoint = API_CLIENT_REQUEST_PDF_DATA(docId);
      } else if (category === PdfCategory.SIGNATURE) {
        endpoint = API_CLIENT_SIGN_PDF_DATA(accountId, snakeCaseToUpperCase(templateType), change ? 'true' : 'false');
      } else if (category === PdfCategory.STATEMENT && docId) {
        endpoint = API_STATEMENT_PDF_DATA(accountId, docId);
      }

      const response = await rootStore.stores.apiStore.get<any>(endpoint);

      if (response?.status === 401) {
        rootStore.stores.userStore.logout();
      } else {
        if (response?.data) {
          setPdfContent(response);
        } else {
          rootStore.stores.errorStore.showErrorDialog('document_sign_pdf_error');
          onError !== undefined && onError();
        }
      }
    }

    if (accountId || preview) {
      getViewPdfContent();
    } else {
      if (useMockData && mockPdfContent[templateType]) {
        setPdfContent({
          //data: mockPdfContent[templateType].data, // MOCK
          data: {}, // NO MOCKS
        } as ApiResponse<T>);
      } else {
        getPdfContent();
      }
    }
  }, [url, templateType, encryptedCredentials, accountId, docId, change, rootStore, onError, preview, category]);

  return pdfContent;
}

export default usePdfContent;
